import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  contentFor,
  effectiveDiscountPercent,
  formatPrice,
  viewerPackagePriceCents,
  type Currency,
  type Locale,
  type Package,
} from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { useFocusTrap } from "@/shared/lib/useFocusTrap";
import { useWholesalePackages } from "../franchise.api";

export interface WholesaleStockModalProps {
  currency: Currency;
  locale: Locale;
  onClose: () => void;
  onSelect: (pkg: Package) => void;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El modal del PRIMER STOCK (Anahí, 26-ago): un solo tab —"Paquetes
 * mayoristas"— y la lista completa desplazándose dentro. Elegir uno lo
 * agrega a la compra de la franquicia; el modal no cobra nada, solo
 * decide.
 *
 * Los paquetes son los de categoría `franchisee` del catálogo real: los
 * mismos que administra el panel, no una lista escrita aquí.
 * ═══════════════════════════════════════════════════════════════════
 */
export function WholesaleStockModal({
  currency,
  locale,
  onClose,
  onSelect,
}: WholesaleStockModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const packages = useWholesalePackages();

  useFocusTrap(panelRef, true, { onEscape: onClose });

  const items = packages.data?.items ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={t("Cerrar")}
        className="absolute inset-0 bg-ds-ink/30 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col rounded-3xl border border-ds-border bg-white shadow-ds-float"
      >
        <header className="shrink-0 border-b border-ds-border p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id={titleId} className="text-[19px] font-semibold text-ds-ink">
                {t("Añade tu primer stock")}
              </h2>
              <p className="mt-1 text-[13px] text-ds-ink-3">
                {t("El inventario con el que arranca tu tienda digital.")}
              </p>
            </div>
            <button
              type="button"
              aria-label={t("Cerrar")}
              onClick={onClose}
              className="rounded-full p-1 text-ds-ink-3 hover:text-ds-ink"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* El ÚNICO tab, como pidió Anahí: los demás no existen aquí. */}
          <div className="mt-4 border-b border-ds-border">
            <span className="inline-block border-b-2 border-ds-violet-pop px-1 pb-2 text-[13px] font-semibold text-ds-violet-pop">
              {t("Paquetes mayoristas")}
            </span>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {packages.isPending ? (
            <p className="text-[13px] text-ds-ink-3">{t("Cargando los paquetes…")}</p>
          ) : packages.isError ? (
            <p className="text-[13px] text-ds-ink-3">
              {t("No se pudieron cargar los paquetes.")}{" "}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => void packages.refetch()}
              >
                {t("Reintentar")}
              </button>
            </p>
          ) : items.length === 0 ? (
            <p className="text-[13px] text-ds-ink-3">
              {t("Todavía no hay paquetes mayoristas publicados.")}
            </p>
          ) : (
            <ul className="grid gap-3">
              {items.map((pkg) => {
                const content = contentFor(pkg.translations, locale);
                // Este es el primer stock CON la franquicia: va con el 10 % (14-sep).
                const price = viewerPackagePriceCents(pkg, currency, { isFranchisee: true });
                const units = pkg.items.reduce((sum, item) => sum + item.quantity, 0);
                return (
                  <li
                    key={pkg.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ds-border p-4"
                  >
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-ds-ink">
                        {content.name}
                        {/*
                          El dato que cambia la cuenta: con este paquete
                          la franquicia va incluida y se paga SOLO el
                          paquete. Ver el desglose de la pantalla.
                        */}
                        {pkg.includesFranchise && (
                          <span className="rounded-ds-pill bg-ds-violet-pop px-2 py-0.5 text-[10px] font-semibold uppercase tracking-ds-label text-white">
                            {t("Franquicia incluida")}
                          </span>
                        )}
                      </p>
                      {content.tagline && (
                        <p className="text-[13px] text-ds-ink-3">{content.tagline}</p>
                      )}
                      <p className="mt-1 text-[12px] text-ds-ink-3">
                        {t("{{units}} unidades · {{pv}} PV · {{off}}% de descuento", {
                          units,
                          pv: pkg.volumePoints,
                          off: effectiveDiscountPercent(pkg, currency, price),
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-[16px] font-semibold text-ds-ink">
                        {typeof price === "number" ? formatPrice(price, currency) : "—"}
                      </p>
                      <Button
                        size="sm"
                        disabled={typeof price !== "number"}
                        onClick={() => onSelect(pkg)}
                      >
                        {t("Elegir")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
